(function () {
  const sectionInstances = new WeakMap();

  class BlogPostsSection {
    constructor(section) {
      this.section = section;
      this.container = section.querySelector(".blog-posts");
      this.layoutEl = this.container?.querySelector(".blog-posts-layout");
      this.wrapper = this.container?.querySelector(".blog-posts-wrapper");

      if (!this.container || !this.layoutEl || !this.wrapper) {
        return;
      }

      this.layout =
        this.layoutEl.dataset.layout || this.container.dataset.layout || "grid";
      this.filterItems = Array.from(
        this.section.querySelectorAll("[data-filter-target]")
      );
      this.allSlides = Array.from(this.wrapper.children);
      this.perRow = Number(this.layoutEl.dataset.cardsPerRow || 1);
      this.breakpointMap = this.buildBreakpointMap();
      this.activeFilter = this.getInitialFilter();
      this.swiper = null;
      this.filterTimeout = null;
      this.resizeObserver = null;

      this.bindFilters();

      if (this.layout === "slider") {
        this.renderSliderSlides(this.activeFilter);
        this.initSwiper();
        this.bindResize();
      } else {
        this.applyGridFilter(this.activeFilter);
      }
    }

    buildBreakpointMap() {
      const steps = [
        { bp: 0, cols: 1 },
        { bp: 750, cols: 2 },
        { bp: 1200, cols: 3 },
        { bp: 1536, cols: 4 },
      ];

      return steps.filter((step) => step.cols <= this.perRow);
    }

    getCurrentCols() {
      const viewportWidth = window.innerWidth;
      let cols = 1;

      this.breakpointMap.forEach((step) => {
        if (viewportWidth >= step.bp) {
          cols = step.cols;
        }
      });

      return cols;
    }

    normalizeFilter(filterTarget) {
      if (!filterTarget || filterTarget === "_all") {
        return null;
      }

      return filterTarget;
    }

    getInitialFilter() {
      const activeFilterItem = this.filterItems.find((item) =>
        item.classList.contains("active")
      );
      const defaultFilterHandle = this.layoutEl.dataset.defaultFilterHandle;

      return this.normalizeFilter(
        activeFilterItem?.dataset.filterTarget || defaultFilterHandle
      );
    }

    matchesFilter(item, filterTarget) {
      if (!filterTarget) {
        return true;
      }

      const productFilter = item.dataset.productFilter || "";
      const filterValues = productFilter
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);

      return filterValues.includes(filterTarget);
    }

    setActiveFilter(filterItem) {
      this.filterItems.forEach((item) => item.classList.remove("active"));

      if (filterItem) {
        filterItem.classList.add("active");
      }
    }

    applyGridFilter(filterTarget) {
      this.allSlides.forEach((item) => {
        item.hidden = !this.matchesFilter(item, filterTarget);
      });
    }

    renderSliderSlides(filterTarget) {
      const fragment = document.createDocumentFragment();
      const filteredSlides = this.allSlides.filter((item) =>
        this.matchesFilter(item, filterTarget)
      );

      this.wrapper.innerHTML = "";

      filteredSlides.forEach((item) => {
        item.hidden = false;
        fragment.appendChild(item);
      });

      this.wrapper.appendChild(fragment);
    }

    updateOverflow() {
      if (this.layout !== "slider") {
        return;
      }

      const visibleSlides =
        this.wrapper.querySelectorAll(".blog-posts-card").length;
      const currentCols = this.getCurrentCols();

      this.layoutEl.classList.toggle(
        "has-overflow",
        visibleSlides > currentCols
      );
    }

    initSwiper() {
      if (this.layout !== "slider" || !this.wrapper.children.length) {
        this.layoutEl.classList.remove("has-overflow");
        return;
      }

      // No `observer`/`observeParents`: MutationObservers on every ancestor re-ran a full swiper
      // update on unrelated DOM churn. Size changes are covered by bindResize's ResizeObserver,
      // content changes by the destroy/re-init on filter switch.
      // Mobile centers the intermediate slides, first/last stay flush to the edges.
      // Must be switched off explicitly at 750 — breakpoint params merge, they don't reset.
      this.swiper = new Swiper(this.layoutEl, {
        slidesPerView: "auto",
        spaceBetween: 16,
        speed: 1000,
        watchOverflow: true,
        centeredSlides: true,
        centeredSlidesBounds: true,
        mousewheel: {
          forceToAxis: true,
          // TODO: consider enabling this if there are issues with mousewheel scrolling within the slider
          // releaseOnEdges: true,
          // thresholdDelta: 10,
        },
        breakpoints: {
          750: {
            spaceBetween: 24,
            centeredSlides: false,
            centeredSlidesBounds: false,
          },
        },
        on: {
          init: () => {
            this.updateOverflow();
          },
          resize: () => {
            this.updateOverflow();
          },
        },
      });
    }

    destroySwiper() {
      const swiperInstance = this.swiper || this.layoutEl.swiper;

      if (swiperInstance && typeof swiperInstance.destroy === "function") {
        swiperInstance.destroy(true, true);
      }

      this.swiper = null;
    }

    switchFilter(filterTarget) {
      const nextFilter = this.normalizeFilter(filterTarget);

      if (this.activeFilter === nextFilter) {
        return;
      }

      this.activeFilter = nextFilter;

      if (this.layout !== "slider") {
        this.applyGridFilter(this.activeFilter);
        return;
      }

      if (this.filterTimeout) {
        window.clearTimeout(this.filterTimeout);
      }

      this.destroySwiper();
      this.renderSliderSlides(this.activeFilter);
      this.initSwiper();
    }

    bindFilters() {
      if (!this.filterItems.length) {
        return;
      }

      this.filterItems.forEach((filterItem) => {
        filterItem.addEventListener("click", () => {
          if (filterItem.classList.contains("active")) {
            return;
          }

          this.setActiveFilter(filterItem);
          this.switchFilter(filterItem.dataset.filterTarget);
        });
      });
    }

    bindResize() {
      if (typeof ResizeObserver === "undefined") {
        return;
      }

      this.resizeObserver = new ResizeObserver(() => {
        if (!this.swiper) {
          return;
        }

        this.swiper.update();
        this.updateOverflow();
      });

      this.resizeObserver.observe(this.layoutEl);
    }

    destroy() {
      if (this.filterTimeout) {
        window.clearTimeout(this.filterTimeout);
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      this.destroySwiper();
    }
  }

  const initBlogPostsSection = (section) => {
    if (!section || !section.classList.contains("section-blog-posts")) {
      return;
    }

    const existingInstance = sectionInstances.get(section);

    if (existingInstance) {
      existingInstance.destroy();
    }

    sectionInstances.set(section, new BlogPostsSection(section));
  };

  // Defer the whole setup until the section approaches the viewport (400px ahead). The observer
  // fires immediately for sections already on screen, so above-the-fold instances initialize
  // right away; below-the-fold ones keep their Swiper init cost out of the page-load window.
  const lazyInitSection = (section) => {
    if (!section || !section.classList.contains("section-blog-posts")) {
      return;
    }
    if (typeof IntersectionObserver !== "function") {
      initBlogPostsSection(section);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        initBlogPostsSection(section);
      },
      { rootMargin: "400px 0px" }
    );
    observer.observe(section);
  };

  lazyInitSection(document.currentScript.parentElement);

  document.addEventListener("shopify:section:load", function (event) {
    lazyInitSection(event.target);
  });
})();
